import { useEffect, useMemo, useState } from 'react'
import AppShell from '@/components/AppShell'
import Skeleton from '@/components/Skeleton'
import { useNotifications } from '@/lib/notifications'
import UnitConversionsDrawer, { Conversion } from '@/components/UnitConversionsDrawer'
import InlineConverter from '@/components/InlineConverter'

type Product = {
  id: number
  treeType: string
  species: string
  subSpecies?: string
  product: string
  productType: string
  certification?: string
  color?: string
  abbr?: string
  conversions?: Conversion[]
}

const LSK = {
  mine: 'products:mine:v1',
} as const

const loadJSON = <T,>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k)
    return v ? (JSON.parse(v) as T) : d
  } catch {
    return d
  }
}
const saveJSON = (k: string, v: any) => localStorage.setItem(k, JSON.stringify(v))

export default function ProductsIndex() {
  const { push } = useNotifications()
  const [tab, setTab] = useState<'My Products' | 'Product Database'>('My Products')
  const [db, setDb] = useState<Product[] | null>(null)
  const [loading, setLoading] = useState(true)

  const [mine, setMine] = useState<Product[]>(loadJSON<Product[]>(LSK.mine, []))
  const [qMine, setQMine] = useState('')
  const [qDb, setQDb] = useState('')

  const [draft, setDraft] = useState<Product>({
    id: Date.now(),
    treeType: '',
    species: '',
    subSpecies: '',
    product: '',
    productType: '',
    certification: '',
    color: '#e5e7eb',
    abbr: '',
    conversions: [],
  })

  const [convFor, setConvFor] = useState<Product | null>(null)

  useEffect(() => saveJSON(LSK.mine, mine), [mine])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const res = await fetch('/products-db.json', { cache: 'no-store' })
        const data: Product[] = await res.json()
        if (!alive) return
        setDb(data)
      } catch (e) {
        console.error(e)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const filteredMine = useMemo(() => {
    if (!qMine.trim()) return mine
    const s = qMine.toLowerCase()
    return mine.filter((p) =>
      [p.treeType, p.species, p.subSpecies, p.product, p.productType, p.certification, p.abbr]
        .join(' ')
        .toLowerCase()
        .includes(s),
    )
  }, [mine, qMine])

  const filteredDb = useMemo(() => {
    if (!db) return []
    if (!qDb.trim()) return db
    const s = qDb.toLowerCase()
    return db.filter((p) =>
      [p.treeType, p.species, p.subSpecies, p.product, p.productType, p.certification]
        .join(' ')
        .toLowerCase()
        .includes(s),
    )
  }, [db, qDb])

  function addDraft() {
    if (!draft.treeType || !draft.species || !draft.product || !draft.productType) {
      push({ title: 'Missing fields', body: 'Tree type, Species, Product, and Category are required.', level: 'warning' })
      return
    }
    setMine((list) => [{ ...draft, id: Date.now() }, ...list])
    push({ title: 'New product added', body: draft.product })
    setDraft({
      id: Date.now(),
      treeType: '',
      species: '',
      subSpecies: '',
      product: '',
      productType: '',
      certification: '',
      color: '#e5e7eb',
      abbr: '',
      conversions: [],
    })
  }

  function saveRow(p: Product) {
    setMine((list) => list.map((x) => (x.id === p.id ? p : x)))
    push({ title: 'Saved product', body: p.product })
  }

  function removeRow(id: number) {
    setMine((list) => list.filter((x) => x.id !== id))
    push({ title: 'Removed product' })
  }

  function addFromDb(p: Product) {
    setMine((list) => {
      if (list.some((x) => isSameCore(x, p))) {
        push({ title: 'Already added', level: 'info' })
        return list
      }
      const copy = {
        ...p,
        id: Date.now(),
        color: '#e5e7eb',
        abbr: p.abbr || '',
        conversions: [],
      }
      push({ title: 'Added to My Products', body: p.product })
      return [copy, ...list]
    })
  }

  function openConversions(p: Product) {
    setConvFor(p)
  }

  function saveConversions(productId: number, rows: Conversion[]) {
    setMine(list => list.map(p => p.id === productId ? { ...p, conversions: rows } : p))
  }

  const Tabs = (
    <div className="flex gap-6 mb-4">
      {(['My Products', 'Product Database'] as const).map((t) => (
        <button
          key={t}
          className={`pb-2 border-b-2 -mb-px ${
            tab === t ? 'border-[var(--brand)] text-[var(--brand)]' : 'border-transparent text-[var(--muted)]'
          }`}
          onClick={() => setTab(t)}
        >
          {t}
        </button>
      ))}
    </div>
  )

  return (
    <AppShell title="My Products">
      {Tabs}

      {tab === 'My Products' && (
        <>
          {/* Add New Product */}
          <div className="card mb-4">
            <div className="card-header">Add New Product</div>
            <div className="card-body grid gap-3">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div>
                  <label className="text-sm">* Tree type</label>
                  <input className="input" value={draft.treeType} onChange={(e) => setDraft({ ...draft, treeType: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">* Species</label>
                  <input className="input" value={draft.species} onChange={(e) => setDraft({ ...draft, species: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">Sub species</label>
                  <input className="input" value={draft.subSpecies || ''} onChange={(e) => setDraft({ ...draft, subSpecies: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">* Category</label>
                  <input className="input" value={draft.productType} onChange={(e) => setDraft({ ...draft, productType: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">* Product Name</label>
                  <input className="input" value={draft.product} onChange={(e) => setDraft({ ...draft, product: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div>
                  <label className="text-sm">Abbreviation</label>
                  <input className="input" value={draft.abbr || ''} onChange={(e) => setDraft({ ...draft, abbr: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">Certification</label>
                  <input className="input" value={draft.certification || ''} onChange={(e) => setDraft({ ...draft, certification: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" className="h-10 w-14 rounded border border-[color:var(--border)]" value={draft.color || '#e5e7eb'} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
                    <input className="input" value={draft.color || ''} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
                  </div>
                </div>
                <div className="lg:col-span-2 flex items-end">
                  <button className="btn" onClick={addDraft}>Save</button>
                </div>
              </div>
            </div>
          </div>

          {/* My Products table */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div>My Products</div>
              <div className="text-sm text-[var(--muted)]">
                Need to add more products to this list? Go to the{' '}
                <button className="underline" onClick={() => setTab('Product Database')}>Product Database</button>.
              </div>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <input className="input w-[420px]" placeholder="Search" value={qMine} onChange={(e) => setQMine(e.target.value)} />
              </div>

              <div className="overflow-auto rounded-xl border border-[color:var(--border)]">
                <table className="min-w-[1280px] w-full text-sm">
                  <thead className="bg-[color:var(--surface-1)]">
                    <tr className="text-left">
                      <th className="px-3 py-2">Tree Type</th>
                      <th className="px-3 py-2">Species</th>
                      <th className="px-3 py-2">Sub Species</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2">Product Type</th>
                      <th className="px-3 py-2">Certification</th>
                      <th className="px-3 py-2">Color</th>
                      <th className="px-3 py-2 w-64">Abbreviation</th>
                      <th className="px-3 py-2 w-24"></th>
                      <th className="px-3 py-2 w-36"></th>
                      <th className="px-3 py-2 w-36">Calc</th>
                      <th className="px-3 py-2 w-36"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMine.length === 0 ? (
                      <tr>
                        <td className="px-3 py-8 text-center text-[var(--muted)]" colSpan={12}>No products yet.</td>
                      </tr>
                    ) : (
                      filteredMine.map((p) => {
                        const count = p.conversions?.length || 0
                        return (
                          <tr key={p.id} className="border-t border-[color:var(--border)]">
                            <td className="px-3 py-2">{p.treeType}</td>
                            <td className="px-3 py-2">{p.species}</td>
                            <td className="px-3 py-2">{p.subSpecies || '-'}</td>
                            <td className="px-3 py-2">{p.product}</td>
                            <td className="px-3 py-2">{p.productType}</td>
                            <td className="px-3 py-2">{p.certification || '-'}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className="inline-block h-4 w-6 rounded" style={{ background: p.color || '#e5e7eb' }} />
                                <input
                                  type="color"
                                  className="h-8 w-10 rounded border border-[color:var(--border)]"
                                  value={p.color || '#e5e7eb'}
                                  onChange={(e) => saveRow({ ...p, color: e.target.value })}
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <input className="input" value={p.abbr || ''} onChange={(e) => saveRow({ ...p, abbr: e.target.value })} />
                            </td>
                            <td className="px-3 py-2">
                              <button className="btn" onClick={() => saveRow(p)}>Save</button>
                            </td>
                            <td className="px-3 py-2">
                              <button className="btn-secondary" onClick={() => openConversions(p)}>
                                Unit Conversions{count > 0 && <span className="ml-1 text-xs px-1 rounded bg-[color:var(--surface-1)] border border-[color:var(--border)]">{count}</span>}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <InlineConverter
                                productName={`${p.product} • ${p.species}`}
                                conversions={p.conversions}
                                onOpenConversions={() => openConversions(p)}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button className="btn-secondary" onClick={() => removeRow(p.id)}>Remove</button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'Product Database' && (
        <div className="card">
          <div className="card-header">Product Database</div>
          <div className="card-body">
            <div className="mb-3">
              <input className="input w-[420px]" placeholder="Search" value={qDb} onChange={(e) => setQDb(e.target.value)} />
            </div>
            {loading || !db ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="overflow-auto rounded-xl border border-[color:var(--border)]">
                <table className="min-w-[1200px] w-full text-sm">
                  <thead className="bg-[color:var(--surface-1)]">
                    <tr className="text-left">
                      <th className="px-3 py-2">Tree Type</th>
                      <th className="px-3 py-2">Species</th>
                      <th className="px-3 py-2">Sub Species</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2">Product Type</th>
                      <th className="px-3 py-2">Certification</th>
                      <th className="px-3 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDb.length === 0 ? (
                      <tr><td className="px-3 py-8 text-center text-[var(--muted)]" colSpan={7}>No results</td></tr>
                    ) : (
                      filteredDb.map((p) => {
                        const inMine = mine.some((x) => isSameCore(x, p))
                        return (
                          <tr key={`${p.id}-${p.product}-${p.species}`} className="border-t border-[color:var(--border)]">
                            <td className="px-3 py-2">{p.treeType}</td>
                            <td className="px-3 py-2">{p.species}</td>
                            <td className="px-3 py-2">{p.subSpecies || '-'}</td>
                            <td className="px-3 py-2">{p.product}</td>
                            <td className="px-3 py-2">{p.productType}</td>
                            <td className="px-3 py-2">{p.certification || '-'}</td>
                            <td className="px-3 py-2">
                              {inMine ? (
                                <button className="btn-secondary" onClick={() => push({ title: 'Already in My Products' })}>Added</button>
                              ) : (
                                <button className="btn" onClick={() => addFromDb(p)}>Add</button>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <UnitConversionsDrawer
        open={!!convFor}
        product={convFor || null}
        onClose={() => setConvFor(null)}
        onSave={saveConversions}
      />
    </AppShell>
  )
}

function isSameCore(a: Product, b: Product) {
  return (
    a.treeType === b.treeType &&
    a.species === b.species &&
    (a.subSpecies || '') === (b.subSpecies || '') &&
    a.product === b.product &&
    a.productType === b.productType
  )
}
